import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that persists state to localStorage
 * @param {string} key - localStorage key
 * @param {*} defaultValue - default value if nothing in localStorage
 * @returns {[any, function]} - [state, setState] like useState
 */
export const usePersistedState = (key, defaultValue) => {
  // Initialize state with value from localStorage or default
  const [state, setState] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Update localStorage when state changes
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(state) : value;
      setState(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [state, setValue];
};

/**
 * Custom hook for debounced localStorage persistence
 * Useful for frequently changing values like scroll position
 * @param {string} key - localStorage key
 * @param {*} defaultValue - default value
 * @param {number} delay - debounce delay in ms
 * @returns {[any, function]} - [state, setState]
 */
export const useDebouncedPersistedState = (key, defaultValue, delay = 300) => {
  const [state, setState] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const timeoutRef = useRef();

  const setValue = (value) => {
    const valueToStore = value instanceof Function ? value(state) : value;
    setState(valueToStore);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout to save to localStorage
    timeoutRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    }, delay);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [state, setValue];
};

/**
 * Hook for managing scroll position persistence with idle detection
 * @param {string} key - localStorage key
 * @param {number} idleDelay - delay before saving scroll position when user stops scrolling (default 3000ms)
 * @returns {object} - { scrollPosition, saveScrollPosition, restoreScrollPosition, resetScrollPosition }
 */
export const useScrollPosition = (key, idleDelay = 3000) => {
  const [scrollPosition, setScrollPosition] = useState(() => {
    try {
      const item = window.localStorage.getItem(`${key}_scroll`);
      return item ? JSON.parse(item) : { top: 0, left: 0 };
    } catch (error) {
      console.warn(`Error reading scroll position from localStorage:`, error);
      return { top: 0, left: 0 };
    }
  });
  
  const timeoutRef = useRef();
  const lastScrollTime = useRef(Date.now());

  const saveScrollPosition = (element) => {
    if (!element) return;

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Update last scroll time
    lastScrollTime.current = Date.now();

    // Only save after user has been idle for specified delay
    timeoutRef.current = setTimeout(() => {
      const timeSinceLastScroll = Date.now() - lastScrollTime.current;
      
      // Only save if user has been idle for the full delay
      if (timeSinceLastScroll >= idleDelay - 100) { // Small buffer for timing
        const newPosition = {
          top: element.scrollTop,
          left: element.scrollLeft
        };
        setScrollPosition(newPosition);
        
        // Save to localStorage immediately when we decide to save
        try {
          window.localStorage.setItem(`${key}_scroll`, JSON.stringify(newPosition));
        } catch (error) {
          console.warn(`Error saving scroll position to localStorage:`, error);
        }
      }
    }, idleDelay);
  };

  const restoreScrollPosition = (element) => {
    if (!element || !scrollPosition) return;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Smooth scroll restoration to reduce glitchiness
      element.scrollTo({
        top: scrollPosition.top || 0,
        left: scrollPosition.left || 0,
        behavior: 'auto' // Use 'auto' instead of 'smooth' for immediate restoration
      });
    });
  };

  const resetScrollPosition = () => {
    const resetPosition = { top: 0, left: 0 };
    setScrollPosition(resetPosition);
    
    try {
      window.localStorage.setItem(`${key}_scroll`, JSON.stringify(resetPosition));
    } catch (error) {
      console.warn(`Error resetting scroll position in localStorage:`, error);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    scrollPosition,
    saveScrollPosition,
    restoreScrollPosition,
    resetScrollPosition
  };
};

export default usePersistedState;